import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Select from 'react-select';
import * as yup from 'yup';
import {ErrorMessage, Form, Formik} from 'formik';
import Button from '../../common/Button';
import Input from '../../common/simple/input';
import { newGraySelectStyles } from '../../common/shared/select.styled';
import Datepicker from '../../common/DatepickerPortal';
import { constants } from '../../common/constants/constants';
import { showLoader, hideLoader } from '../../store/common/slice';
import { fetchBuildingData } from '../../store/buildings/actions';
import arrowInCircle from '../../images/newDesign/arrow_in_white_circle.svg';
import PaymentFlowContainer from './PaymentFlowContainer';
import { StyledReportCollection } from './ReportCollection.styled';
import ServerData from "../../common/serverData";
import {NotificationManager} from "react-notifications";

const ReportCollection = ({ match }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const history = useHistory();
    const { buildingID } = match.params;

    const ltr = useSelector(state => state.common.ltrEnabled);
    const paymentMethod = useSelector(state => state.tenant.paymentMethod);

    const initialValues = {
        apartmentNumber: '',
        name: '',
        phone: '',
        date: new Date(),
        note: ''
    };
    const [apartmentDropdownOptions, setApartmentDropdownOptions] = useState([]);

    const fetchData = async (buildingID) => {
        dispatch(showLoader());
        const buildingData = await dispatch(fetchBuildingData({ buildingID }));

        if (buildingData) {
            const { payload } = buildingData;
            const options = payload.allNumbers?.map(num => ({ value: num, label: num })) ?? [];
            setApartmentDropdownOptions(options);
        }
        dispatch(hideLoader());
    };

    useEffect(async () => {
        await fetchData(buildingID);
    }, []);

    const handleApartmentChange = (e, setFieldValue) => setFieldValue('apartmentNumber', e);

    const formSchema = yup.object().shape({
        apartmentNumber: yup.object()
          .required(t('titles.forms.mandatory_field'))
          .nullable(),
        name: yup.string()
          .required(t('titles.forms.mandatory_field_full_name'))
          .min(2, t('titles.forms.min_length', {size: 2})),
        phone: yup.string()
          .required(t('titles.forms.mandatory_field'))
          .matches(/^[0-9]+$/, t('titles.forms.correct_phone'))
          .min(9, t('titles.forms.min_length', {size: 9}))
          .max(10, t('titles.forms.max_length', {size: 10})),
        date: yup.date()
          .required(t('titles.forms.mandatory_field'))
          .nullable(),
        note: yup.string()
          .max(100, t('titles.forms.max_length_exceeded', 100))
          .optional(),
    });

    async function saveDetailsAndGoToNextPage(data) {
        dispatch(showLoader());
        try {
            const payload = {
                comment: data.note,
                tenantName: data.name,
                phone: data.phone,
                date: data.date,
                paymentMethod,
            };
            const client = new ServerData();
            await client.tenants.payments.chequeCollection(buildingID, data.apartmentNumber.value, payload);
            NotificationManager.success(t('offline_payments.tenant_offline_payment_success'), '', 5000);
            const pathname = `/${constants.pages.confirmationPage}/${buildingID}`;
            history.push({ pathname });
        } catch (e) {
            NotificationManager.error(t('offline_payments.tenant_offline_payment_failed'), '', 5000);
        } finally {
            dispatch(hideLoader());
        }
    }

    const handleFormSubmit = async (values, helpers) => {
        const parsedValues = {
            ...values,
            phone: values.phone.trim(),
        }
        await saveDetailsAndGoToNextPage(parsedValues);
        helpers.reset();      
    };

    return (
        <PaymentFlowContainer title={t('reports.payment_for')} subheader={t('payments.select_date')}>

            <StyledReportCollection className='credit-card-and-actions-block'>
                <Formik
                    initialValues={initialValues}
                    validationSchema={formSchema}
                    enableReinitialize
                    onSubmit={(values, helpers) => handleFormSubmit(values, helpers)}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        setFieldValue,
                        setFieldTouched
                    }) => {
                        return (
                            <Form onSubmit={handleSubmit}>
                                <div>
                                    <label className='label' htmlFor="apartmentNumber">* {t('reports.apt')}</label>
                                    <Select
                                        styles={newGraySelectStyles}
                                        options={apartmentDropdownOptions}
                                        name='apartmentNumber'
                                        onChange={e => handleApartmentChange(e, setFieldValue)}
                                        onBlur={() => setFieldTouched('apartmentNumber', true)}
                                        value={values.apartmentNumber}
                                        placeholder={t('payments.select_placeholder')}
                                        isSearchable={false}
                                    />
                                    <ErrorMessage name="apartmentNumber">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                </div>

                                <div>
                                    <Input
                                        name='name'
                                        label={`* ${t('payments.full_name')}`}
                                        autocomplete='name'
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                    <ErrorMessage name="name">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                </div>

                                <div>
                                    <Input
                                        name='phone'
                                        label={`* ${t('reports.tenantPhone')}`}
                                        autocomplete='tel'
                                        value={values.phone}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        inputMode='tel'
                                    />
                                    <ErrorMessage name="phone">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                </div>

                                <div>
                                    <label className='label' htmlFor="apartmentNumber">* {t('offline_payments.date')}</label>                               
                                    <Datepicker
                                        date={values.date || undefined}
                                        placeholder={'dd/mm/yyyy'}
                                        onChange={selectedDay => setFieldValue('date', selectedDay)}
                                        onClickOutside={() => setFieldTouched('date', true)}
                                        ltr={ltr}
                                        name='date'
                                        withPortal={false} 
                                        showArrow={false}
                                        className='payment-flow' 
                                        minDate={new Date()}
                                    />
                                    <ErrorMessage name="date">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                </div>

                                <div className='full-width'>
                                    <Input
                                        name='note'
                                        label={t('reports.note')}
                                        value={values.note}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                </div>

                                <Button type="submit" onClick={() => {}}>{t('expenses.set_expenses.continue')} <img className='arrow' src={arrowInCircle} /></Button>
                            </Form>
                        )}}
                </Formik>              
            </StyledReportCollection>
        </PaymentFlowContainer>
    )
}

export default ReportCollection;
